import React from 'react'
import { Chip, Tooltip, Typography } from '@material-ui/core'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import { useV4Styles, scheduleMeta, priorityMeta, queueStatusMeta, tokens } from './v4Styles'

const statusDotColors = {
  overdue: '#c62828',
  never_done: '#ad1457',
  needs_completion: '#7b1fa2',
  due_soon: '#e65100',
  ok: '#2e7d32',
  ignored: '#78909c'
}

const QueueCard = ({ item, selected, onClick }) => {
  const classes = useV4Styles()
  const status = scheduleMeta[item.status] || scheduleMeta.ok
  const showPriorityBadge = item.priorityOverride === 'rush' || item.priorityOverride === 'high'
  const showQueueChip = item.queueStatus && item.queueStatus !== 'draft'
  const areaSummary = item.areaSummary || { areaCount: 0, totalAcres: 0 }
  const sprayProgress = item.sprayProgress || {}
  const isMaint = item.hasHerbicideContract
  const needsAspireCompletion = item.needsAspireCompletion
  const activity = item.fieldActivitySummary || {}
  const hasActivity = Number(activity.totalCount || 0) > 0
  const dotColor = statusDotColors[item.status] || statusDotColors.ok
  const hasWaterAccess = item.waterAccess?.useCustomerWater

  return (
    <div
      className={`${classes.queueCard} ${selected ? classes.queueCardSelected : ''}`}
      style={{
        borderLeftColor: selected ? undefined : status.borderColor,
        backgroundColor: selected ? undefined : (isMaint ? 'transparent' : '#fdf6f0')
      }}
      onClick={() => onClick(item._id)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography className={classes.queueCardName}>
          {item.siteName || '(unnamed)'}
        </Typography>
        <div style={{ display: 'flex', gap: 4 }}>
          {showPriorityBadge && (
            <Chip
              size="small"
              label={priorityMeta[item.priorityOverride].label}
              style={{
                backgroundColor: priorityMeta[item.priorityOverride].color,
                color: '#fff',
                height: 18,
                fontSize: 9,
                fontWeight: 700
              }}
            />
          )}
          {showQueueChip && (
            <Chip
              size="small"
              label={queueStatusMeta[item.queueStatus]?.label || item.queueStatus}
              style={{
                backgroundColor: queueStatusMeta[item.queueStatus]?.color || '#78909c',
                color: queueStatusMeta[item.queueStatus]?.textColor || '#fff',
                height: 18,
                fontSize: 9,
                fontWeight: item.queueStatus === 'submitted' ? 700 : undefined,
                border: item.queueStatus === 'submitted' ? '1px solid #E65100' : undefined
              }}
            />
          )}
          {needsAspireCompletion && (
            <Tooltip title="Aspire has hours on this herbicide ticket, but the ticket is not complete.">
              <Chip
                size="small"
                label="Ticket"
                style={{
                  backgroundColor: '#f3e5f5',
                  color: '#7b1fa2',
                  height: 18,
                  fontSize: 9,
                  fontWeight: 700
                }}
              />
            </Tooltip>
          )}
        </div>
      </div>
      <Typography className={classes.queueCardAddress}>{item.address || '\u2014'}</Typography>
      <div className={classes.queueCardMeta}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: status.color, fontSize: 11, fontWeight: 600 }}>
          <span style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: dotColor,
            flexShrink: 0
          }} />
          {status.label}
          {item.daysUntilNext != null && (
            <> &middot; {item.daysUntilNext < 0 ? `${Math.abs(item.daysUntilNext)}d past` : `${item.daysUntilNext}d`}</>
          )}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Tooltip title={hasWaterAccess ? 'Customer water available' : 'No on-site water'}>
            <span style={{ color: hasWaterAccess ? '#1565c0' : '#bdbdbd', fontSize: 11 }}>&#x1F6B0;</span>
          </Tooltip>
          {item.blueDyePreference === 'yes' && (
            <Tooltip title="Blue dye requested">
              <span style={{ color: '#1976d2', fontSize: 11 }}>&#x1F4A7;</span>
            </Tooltip>
          )}
          {isMaint && (
            <Tooltip title="Recurring herbicide work">
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 20, height: 20, borderRadius: '50%',
                backgroundColor: '#e8f5e9',
                color: '#2e7d32'
              }}>
                <AutorenewIcon style={{ fontSize: 13 }} />
              </span>
            </Tooltip>
          )}
        </span>
      </div>
      <div className={classes.queueCardAreas}>
        {areaSummary.areaCount > 0 ? (
          <>
            <span style={{ fontSize: 10, color: tokens.text.secondary }}>{areaSummary.areaCount} areas</span>
            <span style={{ fontSize: 10, color: tokens.text.hint }}>&middot;</span>
            <span style={{ fontSize: 10, color: tokens.text.secondary }}>{areaSummary.totalAcres} acres</span>
            {sprayProgress.operationCount > 0 && (
              <>
                <span style={{ fontSize: 10, color: tokens.text.hint }}>&middot;</span>
                <span style={{ fontSize: 10, color: sprayProgress.status === 'completed' ? '#2e7d32' : '#f57f17', fontWeight: 600 }}>
                  {sprayProgress.completedAreaCount || 0}/{sprayProgress.plannedAreaCount || areaSummary.areaCount} done
                </span>
              </>
            )}
          </>
        ) : (
          <span style={{ fontSize: 10, color: '#e65100', fontStyle: 'italic' }}>No areas mapped</span>
        )}
      </div>
      {hasActivity && (
        <div className={classes.queueCardActivity}>
          {activity.issueCount > 0 && (
            <span className={classes.queueActivityBadge} style={{ color: '#c62828', background: '#ffebee' }}>
              {activity.issueCount} issue{activity.issueCount === 1 ? '' : 's'}
            </span>
          )}
          {activity.photoCount > 0 && (
            <span className={classes.queueActivityBadge} style={{ color: '#1565c0', background: '#e3f2fd' }}>
              {activity.photoCount} photo{activity.photoCount === 1 ? '' : 's'}
            </span>
          )}
          {activity.messageCount > 0 && (
            <span className={classes.queueActivityBadge} style={{ color: '#5d4037', background: '#efebe9' }}>
              {activity.messageCount} msg
            </span>
          )}
          {activity.completionCount > 0 && (
            <span className={classes.queueActivityBadge} style={{ color: '#2e7d32', background: '#e8f5e9' }}>
              Complete
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default QueueCard
